'use client';

import React, { useState, useEffect } from 'react';
import { Avatar } from '@/types/avatar';
import { apiClient } from '@/lib/api-client';
import { useToast } from '@/components/ui/use-toast';
import AvatarPreview from './AvatarPreview';
import AvatarViewer3D from './AvatarViewer3D';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface AvatarGalleryProps {
  userId?: string;
  showActions?: boolean;
  onCreateNew?: () => void;
  className?: string;
}

export default function AvatarGallery({ 
  userId, 
  showActions = true, 
  onCreateNew,
  className = '' 
}: AvatarGalleryProps) {
  const [avatars, setAvatars] = useState<Avatar[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAvatar, setSelectedAvatar] = useState<Avatar | null>(null);
  const [showViewer, setShowViewer] = useState(false);
  const { toast } = useToast();

  // Fetch avatars on component mount
  useEffect(() => {
    fetchAvatars();
  }, []);

  const fetchAvatars = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.getAvatars();
      setAvatars(response.avatars || []);
    } catch (err) {
      setError('Error loading avatars. Please try again later.');
      console.error('Error fetching avatars:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetDefault = async (avatarId: string) => {
    try {
      await apiClient.setDefaultAvatar(avatarId);

      // Update local state
      setAvatars(prev => prev.map(avatar => ({
        ...avatar,
        isDefault: avatar.id === avatarId
      })));

      toast({
        title: "Default Avatar Updated",
        description: "Your default avatar has been changed.",
      });
    } catch (error) {
      console.error('Error setting default avatar:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update default avatar.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (avatarId: string) => {
    if (!confirm('Are you sure you want to delete this avatar?')) return;

    try {
      await apiClient.deleteAvatar(avatarId);

      // Update local state
      setAvatars(prev => prev.filter(avatar => avatar.id !== avatarId));

      toast({
        title: "Avatar Deleted",
        description: "Your avatar has been deleted.",
      });
    } catch (error) {
      console.error('Error deleting avatar:', error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete avatar.",
        variant: "destructive",
      });
    }
  };

  const handleView3D = (avatar: Avatar) => {
    setSelectedAvatar(avatar);
    setShowViewer(true);
  };

  if (isLoading) {
    return (
      <div className={`flex justify-center items-center h-64 ${className}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center text-red-500 p-4 ${className}`}>
        <p>{error}</p>
        <Button onClick={fetchAvatars} variant="outline" className="mt-2">
          Try Again
        </Button>
      </div>
    );
  }

  if (avatars.length === 0) {
    return (
      <div className={`text-center p-10 ${className}`}>
        <Card>
          <CardContent className="p-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2">No Avatars Yet</h3>
            <p className="text-gray-600 mb-4">
              You haven't created any avatars yet. Create your first avatar to get started!
            </p>
            {onCreateNew && (
              <Button onClick={onCreateNew}>
                Create Your First Avatar
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Your Avatars</h2>
        {onCreateNew && (
          <Button onClick={onCreateNew}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create New Avatar
          </Button>
        )}
      </div>

      {/* Avatar Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {avatars.map((avatar) => (
          <AvatarPreview
            key={avatar.id}
            avatar={avatar}
            size="lg"
            showActions={showActions}
            onView3D={() => handleView3D(avatar)}
            onSetDefault={() => handleSetDefault(avatar.id)}
            onDelete={() => handleDelete(avatar.id)}
            isDefault={avatar.isDefault}
          />
        ))}
      </div>

      {/* 3D Viewer Modal */}
      <Dialog open={showViewer} onOpenChange={setShowViewer}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedAvatar?.name}</DialogTitle>
          </DialogHeader>
          {selectedAvatar && (
            <div className="space-y-4">
              <AvatarViewer3D 
                modelUrl={selectedAvatar.modelUrl}
                className="h-96"
                autoRotate={true}
              />
              <div className="text-sm text-gray-600">
                <p><strong>Created:</strong> {new Date(selectedAvatar.createdAt).toLocaleDateString()}</p>
                {selectedAvatar.prompt && (
                  <p><strong>Description:</strong> {selectedAvatar.prompt}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}